import {
  AUTOCOMPLETE_FIELDS,
  DATE_FIELDS,
  DYNAMIC_AUTOCOMPLETE_FIELDS,
  EDITABLE_FIELDS,
  NUMERIC_FIELDS,
  autocompleteFieldConfig,
  staticAutocompleteFieldConfig,
} from "../config";
import { fieldMap, SECTIONS } from "./map-cargo-packing";

export type PackingEditFieldType =
  | "text"
  | "number"
  | "date"
  | "dynamicAutocomplete"
  | "staticAutocomplete";

export type PackingEditField = {
  fieldKey: string;
  label: string;
  cargoKey: string;
  type: PackingEditFieldType;
};

export const PACKING_EDIT_FIELD_KEYS = [
  ...EDITABLE_FIELDS,
  ...NUMERIC_FIELDS,
  ...DATE_FIELDS,
  ...DYNAMIC_AUTOCOMPLETE_FIELDS,
  ...AUTOCOMPLETE_FIELDS,
] as const;

const isPackingEditFieldKey = (fieldKey: string) =>
  (PACKING_EDIT_FIELD_KEYS as readonly string[]).includes(fieldKey);

const getFieldType = (fieldKey: string): PackingEditFieldType => {
  if (DATE_FIELDS.includes(fieldKey as (typeof DATE_FIELDS)[number])) {
    return "date";
  }
  if (NUMERIC_FIELDS.includes(fieldKey as (typeof NUMERIC_FIELDS)[number])) {
    return "number";
  }
  if (
    DYNAMIC_AUTOCOMPLETE_FIELDS.includes(
      fieldKey as (typeof DYNAMIC_AUTOCOMPLETE_FIELDS)[number],
    )
  ) {
    return "dynamicAutocomplete";
  }
  if (
    AUTOCOMPLETE_FIELDS.includes(
      fieldKey as (typeof AUTOCOMPLETE_FIELDS)[number],
    )
  ) {
    return "staticAutocomplete";
  }
  return "text";
};

export const getPackingEditFieldGroups = (
  fieldKeys?: readonly string[],
): {
  title: string;
  color: string;
  fields: PackingEditField[];
}[] => {
  const allowedKeys = fieldKeys
    ? new Set<string>(fieldKeys as readonly string[])
    : null;

  return SECTIONS.map((section) => ({
    title: section.title,
    color: section.color,
    fields: Object.entries(section.fields)
      .filter(
        ([fieldKey]) =>
          isPackingEditFieldKey(fieldKey) &&
          (!allowedKeys || allowedKeys.has(fieldKey)),
      )
      .map(([fieldKey, label]) => ({
        fieldKey,
        label,
        cargoKey: fieldMap[fieldKey as keyof typeof fieldMap],
        type: getFieldType(fieldKey),
      })),
  })).filter((group) => group.fields.length > 0);
};

export const getDestinationLabel = (value: any): string | null => {
  if (!value) return null;
  return [
    value?.customer?.name,
    value?.delivery_city?.name,
    value?.delivery_address,
  ]
    .filter(Boolean)
    .join(", ");
};

export { autocompleteFieldConfig, staticAutocompleteFieldConfig };

const ITEM_FIELD_GETTERS: Record<string, (item: Record<string, any>) => any> = {
  depositor_id: (item) => item.depositor,
  consignee_id: (item) => item.consignee,
  destination_id: (item) => item.destination,
};

export const buildEditingDataFromFieldKeys = (
  item: Record<string, any>,
  fieldKeys: readonly string[],
): Record<string, any> => {
  const result: Record<string, any> = {};
  fieldKeys.forEach((fieldKey) => {
    if (!isPackingEditFieldKey(fieldKey)) return;
    const cargoKey = fieldMap[fieldKey as keyof typeof fieldMap];
    const getter = ITEM_FIELD_GETTERS[cargoKey];
    result[cargoKey] = getter ? getter(item) : item[cargoKey];
  });
  return result;
};

export const buildEditingDataFromItem = (
  item: Record<string, any>,
): Record<string, any> =>
  buildEditingDataFromFieldKeys(item, PACKING_EDIT_FIELD_KEYS);

const normalizeFieldValue = (key: string, value: any) => {
  if (value == null) return value;
  if (key.endsWith("_id") && typeof value === "object") {
    return value.id ?? null;
  }
  return value;
};

export const buildPackingPatchPayload = (
  current: Record<string, any>,
  initial: Record<string, any>,
): Record<string, any> => {
  const payload: Record<string, any> = {};

  Object.keys(current).forEach((key) => {
    const currentValue = normalizeFieldValue(key, current[key]);
    const initialValue = normalizeFieldValue(key, initial[key]);

    if (currentValue === undefined || currentValue === "") return;
    if (currentValue === initialValue) return;

    payload[key] = currentValue;
  });

  return payload;
};
